/**
 * Build-metadata visibility on the client.
 *
 * On app load this:
 *   1. Fetches the frontend `/version.json` (generated at build/startup for
 *      dev/staging; 404 in production).
 *   2. Fetches the backend `/api/v2/health/` (dev/staging only; 404 in prod).
 *   3. Logs both commits so "works on staging, broken in prod" is easy to spot.
 *   4. Stashes both on `window.__APP_VERSIONS__` so error reporters (e.g.
 *      Sentry) can attach them to reports.
 *
 * Everything here is best-effort and swallowed on failure — a missing endpoint
 * (production 404) or network error must never break app startup.
 */

import { REACT_APP_API_ROOT } from "../services";

export interface BuildMetadata {
  status?: string;
  git_commit?: string;
  git_branch?: string;
  build_time?: string;
  environment?: string;
}

export interface AppVersions {
  frontend?: BuildMetadata;
  backend?: BuildMetadata;
}

// Resolve the backend health URL the same way the rest of the app reaches the
// backend: in proxy mode call the relative `/api/...` path (proxied by the dev
// server); otherwise prefix the configured API root (e.g. https://…:8443).
const useProxy = process.env.REACT_APP_USE_PROXY === "true";
const backendHealthUrl = useProxy ? "/api/v2/health/" : `${REACT_APP_API_ROOT.replace(/\/+$/, "")}/api/v2/health/`;

async function fetchJson(url: string): Promise<BuildMetadata | undefined> {
  try {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) return undefined; // 404 in production is expected
    return (await res.json()) as BuildMetadata;
  } catch {
    return undefined;
  }
}

export async function logAppVersions(): Promise<AppVersions> {
  if (typeof window === "undefined") return {};

  const [frontend, backend] = await Promise.all([fetchJson(`${window.location.origin}/version.json`), fetchJson(backendHealthUrl)]);

  const versions: AppVersions = {};
  if (frontend) versions.frontend = frontend;
  if (backend) versions.backend = backend;

  // Expose for error reporters (Sentry `setContext`, etc.).
  (window as unknown as { __APP_VERSIONS__?: AppVersions }).__APP_VERSIONS__ = versions;

  if (frontend || backend) {
    // eslint-disable-next-line no-console
    console.info(
      "[version] frontend:",
      frontend ? `${frontend.git_commit} (${frontend.git_branch})` : "n/a",
      "| backend:",
      backend ? `${backend.git_commit} (${backend.git_branch})` : "n/a"
    );
  }

  return versions;
}
